import React, { FC, useCallback, useMemo, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useFormContext, useFieldArray } from 'react-hook-form'

import { useProducts } from '../serviceHooks'
import { NEW_PRODUCT_ID } from '../constants'
import { AlterNamesModal } from 'modules/ProjectEdit/components'
import { ProductTable, IProductForm, AddButton } from '../components/ProductTable'
import { Menu } from '../components/Menu'
import { useLocalProjectFlags } from 'modulesCommon/serviceHooks'
import { activeProjectVar } from 'modulesCommon/store'

import { IAlterName, IProject } from 'shared/interfaces'
import { ProjectEditContainerProps } from './project-table-container.props'
import styles from './product-table-container.module.scss'

export const ProductTableContainer: FC<ProjectEditContainerProps> = ({ artistName, opusId }) => {
  const [alterNameRow, setAlterNameRow] = useState<number | undefined>()
  const activeProject = useReactiveVar<IProject>(activeProjectVar)
  const { reset, getValues, handleSubmit, setValue } = useFormContext<IProductForm>()
  const { isLocalized } = useLocalProjectFlags(activeProject)

  const {
    createProductItem,
    removeProduct,
    updateProduct,
    removeAlterName,
    createAlterName
  } = useProducts((data) => {
    reset({ products: data, artistName, opusId })
  })

  const { fields, remove, append, update } = useFieldArray<IProductForm, 'products', 'products'>({
    name: 'products'
  })

  const onSave = useCallback<(data: IProductForm) => void>(
    ({ editRow, products }) => {
      if (typeof editRow === 'number') {
        const { id, ...item } = products[editRow]
        update(editRow, { id, ...item })
        setValue('editRow', undefined)

        if (id === NEW_PRODUCT_ID) {
          createProductItem({
            configuration: item.configuration,
            name: item.name,
            publish_date: item.publish_date,
            gps_status: item.gps_status ?? '',
            ...(item.gpid_list && { gpid_list: item.gpid_list })
          })
        } else {
          updateProduct({ id, ...item })
        }
      }
    },
  [createProductItem, setValue, updateProduct, update]
  )
  const handleRemove = useCallback<(idx: number) => void>(
    (idx) => {
      const { id } = getValues(`products.${idx}`)
      removeProduct(id)
      remove(idx)
    },
  [getValues, remove, removeProduct]
  )

  const alterNameProduct = useMemo(
    () => (alterNameRow !== undefined ? fields[alterNameRow] : null),
    [alterNameRow, fields]
  )
  const onOpenAlterNames = useCallback<(idx: number) => void>((idx: number) => setAlterNameRow(idx), [setAlterNameRow])
  const onCloseAlterName = useCallback(() => {
    setAlterNameRow(undefined)
  }, [setAlterNameRow])

  const onCreateAlterName = useCallback(async (alterName: IAlterName) => {
    const { data } = await createAlterName(alterName)
    if (alterNameRow !== undefined && data?.createAlterName) {
      update(alterNameRow, {
        ...fields[alterNameRow],
        alter_names: [...(fields[alterNameRow]?.alter_names ?? []) as IAlterName[], data.createAlterName]
      })
    }
  }, [createAlterName, fields, update, alterNameRow])

  const onDeleteAlterName = useCallback(async (id: string) => {
    if (alterNameRow !== undefined) {
      update(alterNameRow, {
        ...fields[alterNameRow],
        alter_names: fields[alterNameRow].alter_names?.filter((a) => a.id !== id)
      })
      await removeAlterName(id)
    }
  }, [removeAlterName, fields, update, alterNameRow])

  return (
    <section className={styles.section}>
      <h3 className={styles.title}>Products</h3>
      {fields.length ? (
        <form onSubmit={handleSubmit(onSave)}>
          <ProductTable data={fields} fields={['name', 'configuration', 'gps_status', 'publish_date']}>
            {(_, idx) =>
              <Menu
                idx={idx}
                onDelete={handleRemove}
                onAlterNames={onOpenAlterNames}
                disabledEdit={isLocalized}
              />
            }
          </ProductTable>
        </form>
      ) : null}
      <div className={styles.btns}>
        <AddButton append={append} disabled={isLocalized} />
      </div>
      {
        alterNameRow !== undefined && (
          <AlterNamesModal
            productId={alterNameProduct?.parent_id ?? ''}
            productName={alterNameProduct?.name ?? ''}
            onClose={onCloseAlterName}
            alterNameList={alterNameProduct?.alter_names ?? []}
            onDeleteAlterName={onDeleteAlterName}
            onCreateAlterName={onCreateAlterName}
          />
        )
      }
    </section>
  )
}
